/**
 * MermaidDiagram Component - Simple & Reliable
 *
 * Renders Mermaid diagrams using direct DOM manipulation
 * No complex state management or lazy loading
 */

import React, { useEffect, useRef } from 'react';
import mermaid from 'mermaid';

interface MermaidDiagramProps {
  code: string;
  language: string;
}

// Initialize Mermaid once globally
let mermaidInitialized = false;
if (!mermaidInitialized) {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    securityLevel: 'loose',
    fontFamily: 'ui-monospace, monospace',
  });
  mermaidInitialized = true;
}

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ code, language }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgId = useRef(`mermaid-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current) return;

      try {
        // Clear previous content
        containerRef.current.innerHTML = '';

        // Render diagram directly into container
        const { svg } = await mermaid.render(svgId.current, code);

        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
      } catch (error) {
        // Silently handle syntax errors - these are common with AI-generated diagrams
        // Only log in development mode
        if (import.meta.env.DEV) {
          console.warn('Mermaid diagram syntax error (displaying as code block):',
            error instanceof Error ? error.message : String(error)
          );
        }

        // Show clean code block fallback without error styling
        if (containerRef.current) {
          containerRef.current.innerHTML = `
            <pre class="text-sm text-slate-300 overflow-x-auto p-4 bg-slate-800/50 rounded font-mono whitespace-pre-wrap">${
              code.replace(/</g, '&lt;').replace(/>/g, '&gt;')
            }</pre>
          `;
        }
      }
    };

    renderDiagram();
  }, [code]);

  return (
    <div className="my-4 border border-slate-700/50 rounded-lg overflow-hidden">
      {/* Simple header */}
      <div className="px-4 py-2 bg-slate-800/50 border-b border-slate-700/50">
        <div className="flex items-center space-x-2">
          <svg
            className="w-4 h-4 text-cyan-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <span className="text-sm font-medium text-slate-300">
            {language.charAt(0).toUpperCase() + language.slice(1)} Diagram
          </span>
        </div>
      </div>

      {/* Diagram container */}
      <div className="p-4 bg-slate-900/30">
        <div ref={containerRef} className="mermaid-container overflow-x-auto" />
      </div>
    </div>
  );
};
